import { type ReactNode } from 'react';
import { LockIcon } from 'lucide-react';
import { t } from '../../i18n';
import { Icon } from '../Icon';

export interface EmptyStateProps {
  title?: ReactNode;
  description?: ReactNode;
  illustration?: ReactNode;
  action?: ReactNode;
  /**
   * `panel` — tinted recessed well (`bg-state-empty-bg`), pairs with an illustration.
   * `inline` — no container fill or radius; the title + action sit directly on the
   * parent surface.
   * `locked` — same tinted well as `panel`, but muted text + a default lock icon
   * (unless `illustration` overrides it) for "this feature isn't on your plan"
   * states (P12c entitlement cascade — see WatchlistPanel/ForecastPanel for the
   * reference copy: "X is not available on your current plan.").
   *
   * Default: `panel` when an `illustration` is present, otherwise `inline` — so a
   * bare "no results" state no longer renders the dated "padded card with nothing
   * in it" (V1 parity). Pass `variant` explicitly to override the heuristic.
   */
  variant?: 'panel' | 'inline' | 'locked';
  /**
   * Optional teaching/tip callout rendered below the action — a short "here's how"
   * hint (e.g. "Tip: you can import from a CSV"). Rendered in an info-pale box
   * (`bg-info-pale text-info-text`, `--radius-control`). Purely additive: omitting
   * it is byte-identical to the pre-`tip` primitive.
   */
  tip?: ReactNode | undefined;
  className?: string;
}

export function EmptyState({
  title = t('emptyState.title'),
  description,
  illustration,
  action,
  variant,
  tip,
  className,
}: EmptyStateProps) {
  const resolved = variant ?? (illustration ? 'panel' : 'inline');
  const locked = resolved === 'locked';
  const base = 'flex flex-col items-center justify-center text-center';
  const variantCls =
    resolved === 'inline'
      ? 'py-10 text-secondary'
      : locked
        ? 'p-8 bg-state-empty-bg text-muted rounded-control'
        : 'p-8 bg-state-empty-bg text-state-empty-fg rounded-control';
  const resolvedIllustration =
    illustration ?? (locked ? <Icon as={LockIcon} size="lg" className="text-muted" /> : undefined);
  return (
    <div className={[base, variantCls, className].filter(Boolean).join(' ')} role="status">
      {resolvedIllustration && <div className="mb-4">{resolvedIllustration}</div>}
      <h3 className={locked ? 'text-heading-3 text-muted' : 'text-heading-3 text-primary'}>
        {title}
      </h3>
      {description && <p className="mt-2 text-body-sm text-secondary">{description}</p>}
      {action && <div className="mt-6">{action}</div>}
      {tip && (
        <div className="mt-4 rounded-control bg-info-pale px-3 py-2 text-body-sm text-info-text">
          {tip}
        </div>
      )}
    </div>
  );
}
